import React from 'react';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { CheckCircle2, Shield, Award, Star } from 'lucide-react';
import { cn } from '../lib/utils';

/**
 * AvatarWithBadge - Avatar component with verification ring and badge
 * Displays user avatar with optional verification indicators
 * 
 * @param {Object} props
 * @param {string} props.src - Avatar image URL
 * @param {string} props.alt - Alt text for avatar image
 * @param {string} props.fallback - Fallback text (initials)
 * @param {string} props.size - Avatar size: 'xs', 'sm', 'md', 'lg', 'xl' (default: 'md')
 * @param {boolean} props.verified - Whether user is verified
 * @param {string} props.verificationType - Type of verification: 'id', 'background', 'premium', 'trusted'
 * @param {boolean} props.showRing - Whether to show verification ring (default: true)
 * @param {string} props.ringColor - Custom ring color class
 * @param {string} props.badgePosition - Badge position: 'top-right', 'bottom-right', 'bottom-left', 'top-left'
 * @param {string} props.className - Additional CSS classes
 * @param {Function} props.onClick - Click handler
 */
const AvatarWithBadge = ({
  src,
  alt = 'User avatar',
  fallback,
  size = 'md',
  verified = false,
  verificationType = 'id',
  showRing = true,
  ringColor,
  badgePosition = 'bottom-right',
  className,
  onClick,
  ...props
}) => {
  // Size configurations
  const sizeClasses = {
    xs: 'w-8 h-8',
    sm: 'w-10 h-10',
    md: 'w-12 h-12',
    lg: 'w-16 h-16',
    xl: 'w-24 h-24',
    '2xl': 'w-32 h-32',
  };

  const badgeSizes = {
    xs: 'w-3 h-3',
    sm: 'w-3.5 h-3.5',
    md: 'w-4 h-4',
    lg: 'w-5 h-5',
    xl: 'w-6 h-6',
    '2xl': 'w-8 h-8',
  };

  const badgeContainerSizes = {
    xs: 'p-0.5',
    sm: 'p-0.5',
    md: 'p-1',
    lg: 'p-1.5',
    xl: 'p-2',
    '2xl': 'p-2.5',
  };

  // Badge configuration based on verification type
  const badgeConfig = {
    id: {
      icon: CheckCircle2,
      color: 'bg-success-500',
      ringColor: 'ring-success-500',
    },
    background: {
      icon: Shield,
      color: 'bg-primary-500',
      ringColor: 'ring-primary-500',
    },
    premium: {
      icon: Award,
      color: 'bg-secondary-500',
      ringColor: 'ring-secondary-500',
    },
    trusted: {
      icon: Star,
      color: 'bg-secondary-500',
      ringColor: 'ring-secondary-500',
    },
  };

  const config = badgeConfig[verificationType] || badgeConfig.id;
  const BadgeIcon = config.icon;

  // Badge position classes
  const positionClasses = {
    'top-right': '-top-1 -right-1',
    'bottom-right': '-bottom-1 -right-1',
    'bottom-left': '-bottom-1 -left-1',
    'top-left': '-top-1 -left-1',
  };

  return (
    <div 
      className={cn('relative inline-block', className)}
      onClick={onClick}
      {...props}
    >
      {/* Avatar with optional verification ring */}
      <div className={cn(
        'relative',
        verified && showRing && 'ring-4',
        verified && showRing && (ringColor || config.ringColor),
        'rounded-full'
      )}>
        <Avatar className={cn(
          sizeClasses[size],
          'border-2 border-background',
          onClick && 'cursor-pointer hover:opacity-90 transition-opacity'
        )}>
          <AvatarImage src={src} alt={alt} />
          <AvatarFallback className="bg-primary-500 text-primary-foreground font-semibold">
            {fallback || '??'}
          </AvatarFallback>
        </Avatar>
      </div>

      {/* Verification Badge */}
      {verified && (
        <div className={cn(
          'absolute rounded-full border-2 border-background',
          config.color,
          badgeContainerSizes[size],
          positionClasses[badgePosition]
        )}>
          <BadgeIcon className={cn(
            badgeSizes[size],
            'text-white'
          )} />
        </div>
      )}
    </div>
  );
};

/**
 * AvatarGroup - Group of overlapping avatars
 * Displays multiple avatars in an overlapping layout
 * 
 * @param {Object} props
 * @param {Array} props.avatars - Array of avatar configurations
 * @param {number} props.max - Maximum number of avatars to show (default: 5)
 * @param {string} props.size - Size for all avatars
 * @param {string} props.className - Additional CSS classes
 */
export const AvatarGroup = ({
  avatars = [],
  max = 5,
  size = 'md',
  className,
  ...props
}) => {
  const displayAvatars = avatars.slice(0, max);
  const remainingCount = avatars.length - max;

  return (
    <div className={cn('flex -space-x-3', className)} {...props}>
      {displayAvatars.map((avatar, index) => (
        <AvatarWithBadge
          key={index}
          src={avatar.src}
          alt={avatar.alt}
          fallback={avatar.fallback}
          size={size}
          verified={avatar.verified}
          verificationType={avatar.verificationType}
          showRing={avatar.showRing}
          className="ring-2 ring-background"
        />
      ))}
      {remainingCount > 0 && (
        <Avatar className={cn(
          size === 'xs' && 'w-8 h-8',
          size === 'sm' && 'w-10 h-10',
          size === 'md' && 'w-12 h-12',
          size === 'lg' && 'w-16 h-16',
          size === 'xl' && 'w-24 h-24',
          'ring-2 ring-background'
        )}>
          <AvatarFallback className="bg-neutral-200 text-neutral-700 font-semibold">
            +{remainingCount}
          </AvatarFallback>
        </Avatar>
      )}
    </div>
  );
};

export default AvatarWithBadge;